/**
 * The till's outbound queue (`08-pos-terminal-and-qr.md` § Offline mode).
 *
 * A restaurant's internet goes down mid-service, and service does not stop. So
 * the till writes every mutation to a local queue first and drains it when the
 * network comes back — which makes the queue, not the network, the thing that
 * has to be right.
 *
 * The rules the spec fixes, and what each one costs if broken:
 *
 *  - **A client-generated UUID is the idempotency key.** The same operation
 *    replayed after a timeout must not become a second bill. The API takes
 *    `Idempotency-Key` for exactly this and answers with the original order.
 *  - **Order matters.** "Add items" before "pay" is not the same service as
 *    "pay" before "add items", so the queue drains strictly in sequence and a
 *    failure stops the drain rather than skipping past it.
 *  - **Last-write-wins never applies to money.** A paid offline order is
 *    accepted server-side; nothing here silently drops a settlement because
 *    the server disagreed.
 *  - **Sync is background and non-blocking.** Draining never blocks the till,
 *    which is why this is a plain state machine over a storage interface
 *    rather than anything that owns a thread or a screen.
 *
 * Deliberately storage-agnostic: the shell backs it with IndexedDB, the tests
 * back it with a map, and neither knows about the other.
 */

export type QueuedOperation = {
  /** Client-generated, and the `Idempotency-Key` the server sees. */
  readonly id: string;
  /** Monotonic per device, so a drain replays in the order service happened. */
  readonly sequence: number;
  readonly method: "POST" | "PATCH" | "PUT" | "DELETE";
  readonly path: string;
  readonly body: unknown;
  /** When the till did it, not when the server heard about it. */
  readonly queuedAt: string;
  /**
   * The cashier's assertion as it stood when they did it.
   *
   * Captured here rather than read at drain time because authority is a
   * property of the moment the bill was rung, not of the moment the network
   * came back. A queue that sat overnight would otherwise replay under
   * whoever happens to be at the till in the morning, or — once the twelve
   * hours had run out — under nobody, and the till would drop a real bill.
   * Null for an operation taken before anyone had signed in at the till,
   * which is what the terminal's own authority is still for.
   */
  readonly staffToken: string | null;
  readonly attempts: number;
  readonly lastError: string | null;
  /**
   * A settlement. Flagged because § Offline mode says money is never resolved
   * by last-write-wins: these are surfaced rather than discarded when they
   * fail, however old they are.
   */
  readonly isMoney: boolean;
  /**
   * An operation that cannot be addressed until an earlier one has been sent.
   *
   * Settling a bill taken offline is exactly this: the order is created by one
   * call and paid by another, and the id in the second path is a thing only
   * the server knows. So the dependency is **declared as data** — a token in
   * the path and the field of the earlier response that fills it — because a
   * callback cannot be written to IndexedDB and survive the app being killed.
   */
  readonly dependsOn?: {
    readonly operationId: string;
    /** The field to read out of that operation's response. */
    readonly field: string;
    /** What to replace in this operation's path. */
    readonly token: string;
  };
};

/**
 * A refusal, kept where it will survive the tab being closed.
 *
 * `rejectedAt` is when the till learned, not when the operation was queued —
 * `operation.queuedAt` still holds that, and the gap between the two is
 * usually the outage.
 */
export type RejectedOperation = {
  readonly operation: QueuedOperation;
  readonly status: number;
  readonly message: string;
  readonly rejectedAt: string;
};

export type QueueStorage = {
  all(): Promise<QueuedOperation[]>;
  put(operation: QueuedOperation): Promise<void>;
  remove(id: string): Promise<void>;
  /**
   * The dead-letter store.
   *
   * A refused operation used to leave durable storage and live in an array on
   * a class, which meant a refused settlement — somebody's money — survived
   * exactly as long as the tab did. Reloading before the cashier read the
   * message swallowed it, which is the outcome the whole module exists to
   * prevent. Keyed by the operation's id, so replaying a refusal overwrites
   * rather than accumulating.
   */
  reject(entry: RejectedOperation): Promise<void>;
  rejected(): Promise<RejectedOperation[]>;
  /** Dismissed by a person who has dealt with it. The only way one leaves. */
  clearRejection(id: string): Promise<void>;
};

export type SendResult =
  | { readonly outcome: "sent"; readonly response: unknown }
  /** The network is gone. Not the operation's fault; stop and try later. */
  | { readonly outcome: "offline" }
  /**
   * The server refused it and always will — a 4xx that is not a timeout. The
   * operation leaves the queue and is handed back to be shown to somebody,
   * because a silently dropped bill is worse than a visible failure.
   */
  | { readonly outcome: "rejected"; readonly status: number; readonly message: string };

export type Transport = (operation: QueuedOperation) => Promise<SendResult>;

/**
 * Told what the server answered, for an operation that has just gone.
 *
 * The queue's own way of naming a later operation is `dependsOn`, and it only
 * reaches back as far as the queue does — once an operation has been sent
 * there is nothing left to resolve against. That is fine for a bill created
 * and paid in one breath, and not fine for one opened when a table is seated
 * and added to for the next hour. So the answer is handed out instead, and the
 * caller keeps the id.
 */
export type OnSent = (operation: QueuedOperation, response: unknown) => void | Promise<void>;

export type DrainReport = {
  readonly sent: number;
  readonly remaining: number;
  /** Refused for good, and needing a person. */
  readonly rejected: { operation: QueuedOperation; status: number; message: string }[];
  readonly stoppedBecauseOffline: boolean;
};

export class OutboundQueue {
  #storage: QueueStorage;
  #transport: Transport;
  #draining = false;
  #sequence = 0;

  readonly #onSent: OnSent | undefined;

  constructor(storage: QueueStorage, transport: Transport, onSent?: OnSent) {
    this.#storage = storage;
    this.#transport = transport;
    this.#onSent = onSent;
  }

  /** Picks up where a previous run left off, after the app was killed. */
  async resume(): Promise<void> {
    const pending = await this.#storage.all();
    this.#sequence = pending.reduce((highest, row) => Math.max(highest, row.sequence), 0);
  }

  async enqueue(
    input: {
      method: QueuedOperation["method"];
      path: string;
      body?: unknown;
      isMoney?: boolean;
      staffToken?: string | null;
      dependsOn?: QueuedOperation["dependsOn"];
    },
    id: string,
    now: Date,
  ): Promise<QueuedOperation> {
    this.#sequence += 1;
    const operation: QueuedOperation = {
      id,
      sequence: this.#sequence,
      method: input.method,
      path: input.path,
      body: input.body ?? null,
      queuedAt: now.toISOString(),
      staffToken: input.staffToken ?? null,
      attempts: 0,
      lastError: null,
      isMoney: input.isMoney ?? false,
      ...(input.dependsOn ? { dependsOn: input.dependsOn } : {}),
    };
    await this.#storage.put(operation);
    return operation;
  }

  /**
   * Takes back an operation that has not gone yet, and anything waiting on it.
   *
   * A table seated by mistake and cleared a moment later, while the till was
   * offline, never reached the server at all — so the honest undo is to stop
   * it leaving rather than to send it and then send a cancellation for a bill
   * nobody outside this device ever saw.
   *
   * Returns what it actually removed, because "nothing" and "the create had
   * already gone" are different situations with different next moves, and the
   * caller cannot tell them apart otherwise.
   */
  async discard(id: string): Promise<QueuedOperation[]> {
    const held = await this.pending();
    const operation = held.find((row) => row.id === id);
    if (!operation) return [];

    const removed = [operation, ...(await this.#orphansOf(id))];
    for (const row of removed) await this.#storage.remove(row.id);
    return removed;
  }

  async pending(): Promise<QueuedOperation[]> {
    const rows = await this.#storage.all();
    return rows.sort((a, b) => a.sequence - b.sequence);
  }

  /**
   * What the server refused and nobody has dealt with yet, oldest first.
   *
   * Read from storage rather than remembered, so it survives the app being
   * killed — which is the whole difference between reporting a refused
   * settlement and losing one.
   */
  async rejected(): Promise<RejectedOperation[]> {
    const rows = await this.#storage.rejected();
    return rows.sort((a, b) => a.operation.sequence - b.operation.sequence);
  }

  /** A person has dealt with it. The only way a refusal leaves the store. */
  async dismissRejection(id: string): Promise<void> {
    await this.#storage.clearRejection(id);
  }

  /**
   * Sends what is queued, oldest first, and stops at the first sign the
   * network is gone.
   *
   * Re-entrant by refusal rather than by locking: a second drain while one is
   * running would send the same operation twice, and the whole point of the
   * queue is that it does not.
   */
  async drain(): Promise<DrainReport> {
    if (this.#draining) {
      const waiting = await this.pending();
      return { sent: 0, remaining: waiting.length, rejected: [], stoppedBecauseOffline: false };
    }
    this.#draining = true;

    const rejected: DrainReport["rejected"] = [];
    let sent = 0;
    let stoppedBecauseOffline = false;

    try {
      // Re-read each time rather than iterating a snapshot: sending one
      // operation resolves the ones queued behind it, and a snapshot would
      // still be holding their unresolved copies.
      for (;;) {
        const [operation] = await this.pending();
        if (!operation) break;

        if (operation.dependsOn) {
          const waitingFor = operation.dependsOn.operationId;
          const stillComing = (await this.pending()).some((row) => row.id === waitingFor);

          if (stillComing) {
            // It is behind something that has not gone yet. The queue is in
            // order, so there is nothing further along worth trying either.
            break;
          }

          /**
           * Its dependency is not in the queue and never resolved this one,
           * which means it left without an answer this could use. Waiting
           * would block the queue forever and hide a payment; it goes back to
           * a person instead.
           */
          await this.#storage.remove(operation.id);
          rejected.push({
            operation,
            status: 0,
            message: "The order this belonged to was never created.",
          });
          continue;
        }

        const result = await this.#transport(operation);

        if (result.outcome === "offline") {
          // Everything after this one is younger, and order matters.
          await this.#storage.put({
            ...operation,
            attempts: operation.attempts + 1,
            lastError: "offline",
          });
          stoppedBecauseOffline = true;
          break;
        }

        if (result.outcome === "rejected") {
          rejected.push(await this.#deadLetter(operation, result.status, result.message));
          // Whatever was waiting on it can never be addressed. Dropping those
          // silently would lose a payment, so they are kept too.
          for (const orphan of await this.#orphansOf(operation.id)) {
            rejected.push(
              await this.#deadLetter(orphan, 0, "The order this belonged to was never created."),
            );
          }
          // A refusal is about this operation, not the connection, so the
          // drain carries on — but a refused *settlement* is somebody's money
          // and is kept rather than swallowed.
          continue;
        }

        await this.#storage.remove(operation.id);
        // Anything queued behind this that was waiting on it is resolved now,
        // **into storage** rather than into a variable — the app may be killed
        // between this drain and the next, and a payment that lost its order
        // id would be unrecoverable.
        await this.#resolveDependents(operation.id, result.response);
        // Then, and only then, the answer goes to whoever queued it. The order
        // matters and is not tidiness: anything enqueued before this point is
        // resolved by the line above, and anything enqueued after it can be
        // addressed directly because the caller now knows the id. Swap the two
        // and there is a window where neither is true and the work is lost.
        await this.#onSent?.(operation, result.response);
        sent += 1;
      }
    } finally {
      this.#draining = false;
    }

    return {
      sent,
      remaining: (await this.pending()).length,
      rejected,
      stoppedBecauseOffline,
    };
  }

  /**
   * Fills in what was waiting on an operation that has just been sent.
   *
   * Written back to storage rather than held in a variable: the app may be
   * killed between one drain and the next, and a payment that lost its order
   * id would be unrecoverable.
   */
  async #resolveDependents(operationId: string, response: unknown): Promise<void> {
    const answer = (response ?? null) as Record<string, unknown> | null;

    for (const waiting of await this.pending()) {
      const dependsOn = waiting.dependsOn;
      if (dependsOn?.operationId !== operationId) continue;

      const field = answer?.[dependsOn.field];
      if (typeof field !== "string") continue;

      const { dependsOn: _resolved, ...rest } = waiting;
      await this.#storage.put({
        ...rest,
        path: waiting.path.replaceAll(dependsOn.token, field),
      });
    }
  }

  async #orphansOf(operationId: string): Promise<QueuedOperation[]> {
    return (await this.pending()).filter((row) => row.dependsOn?.operationId === operationId);
  }

  /**
   * Into the dead-letter store, then out of the outbound one.
   *
   * That order is the same discipline `#resolveDependents` is written to and
   * for the same reason: a kill between the two lines leaves the operation
   * still queued, so it is replayed, refused again, and lands on its own id.
   * The other order loses it outright.
   */
  async #deadLetter(
    operation: QueuedOperation,
    status: number,
    message: string,
  ): Promise<RejectedOperation> {
    const entry: RejectedOperation = {
      operation,
      status,
      message,
      rejectedAt: new Date().toISOString(),
    };
    await this.#storage.reject(entry);
    await this.#storage.remove(operation.id);
    return entry;
  }
}

/**
 * How long to wait before trying again, in milliseconds.
 *
 * Exponential with a ceiling, because a till that retries every second on a
 * dead connection is a till whose battery dies before the service ends. The
 * jitter stops twenty tills in a food court retrying in lockstep.
 */
export function backoffMs(attempts: number, random: () => number): number {
  const base = Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5));
  return Math.round(base * (0.5 + random() * 0.5));
}
